import { T } from "../lib/globals";
import React, { useState } from "react";
import ModuleHeaderMenu from "../components/ModuleHeaderMenu";
import { sortAlpha } from "../lib/sortUtils";
import { markOperationalUpdatePending } from "../lib/operationalProvisional";
import { StatCard, useToast } from "../components/ui";

export function createEquipamentosEPIsModule(deps) {
  const {
    useFarm,
    useFarmCollection,
    useReliableSyncOnSave,
    ITEM_CONDITIONS,
  } = deps;

  function EquipamentosEPIsModule({ session }) {
  const { fid: currentFarmId } = useFarm();
  const syncOnSave = useReliableSyncOnSave();
  const [checklists, setChecklists] = useFarmCollection("checklists", []);
// eslint-disable-next-line no-unused-vars
  const [inspections, _setInspections] = useFarmCollection("inspections", []);
  const [employees]  = useFarmCollection("employees", []);
  const [tab, setTab] = useState("epis");
// eslint-disable-next-line no-unused-vars
  const [toast, _show] = useToast();
  const moduleCardsConfig = (()=>{
    try{
      const parsed = JSON.parse(localStorage.getItem("fz:moduleCardsConfig") || "null");
      return parsed && typeof parsed === "object" ? parsed : {};
    }catch(_){ return {}; }
  })();
  const getConfiguredSectionCards = React.useCallback((sectionId, fallback=[])=>{
    const moduleConfig = moduleCardsConfig?.my_items;
    if(moduleConfig && Object.prototype.hasOwnProperty.call(moduleConfig, sectionId)){
      const configured = moduleConfig?.[sectionId];
      if(Array.isArray(configured)) return configured.map(v=>String(v||""));
      return [];
    }
    return (Array.isArray(fallback) ? fallback : []).map(v=>String(v||""));
  }, [moduleCardsConfig]);
  const allowedRootCards = React.useMemo(()=>{
    const direct = getConfiguredSectionCards("moduleHome", []);
    return Array.isArray(direct) && direct.length ? direct : ["equipamentos","epis","vistorias","itens_visualizados"];
  }, [getConfiguredSectionCards]);

  const sessionIdentity = {
    accountId: String(session?.id || "").trim(),
    userId: String(session?.userId || "").trim(),
    username: String(session?.username || "").trim().toLowerCase(),
    name: String(session?.name || "").trim().toLowerCase(),
  };

  const employeeMatchesSession = (emp) => {
    const ids = [emp?.id, emp?.employeeId, emp?.linkedAccountId, emp?.accountId, emp?.userId].map(v => String(v || "").trim()).filter(Boolean);
    if (ids.some(v => v === sessionIdentity.accountId || v === sessionIdentity.userId)) return true;
    const username = String(emp?.username || "").trim().toLowerCase();
    if (username && username === sessionIdentity.username) return true;
    const fullName = String(emp?.name || "").trim().toLowerCase();
    if (fullName && fullName === sessionIdentity.name) return true;
    return false;
  };

  const myEmployee = employees.find(employeeMatchesSession);
  const myIdentity = {
    employeeId: String(myEmployee?.id || "").trim(),
    accountId: sessionIdentity.accountId,
    userId: sessionIdentity.userId,
    linkedAccountId: String(myEmployee?.linkedAccountId || myEmployee?.accountId || "").trim(),
    username: sessionIdentity.username,
    name: sessionIdentity.name,
  };
  const myEmpId = myEmployee?.id ?? null;

  const checklistMatchesMe = (list) => {
    const ids = [list?.empId, list?.employeeId, list?.linkedAccountId, list?.accountId, list?.userId].map(v => String(v || "").trim()).filter(Boolean);
    if (ids.some(v => v === myIdentity.employeeId || v === myIdentity.accountId || v === myIdentity.userId || v === myIdentity.linkedAccountId)) return true;
    const names = [list?.empName, list?.employeeName, list?.name].map(v => String(v || "").trim().toLowerCase()).filter(Boolean);
    return names.includes(myIdentity.name);
  };

  // Minha lista de EPIs/Equipamentos
  const myList = checklists.find(checklistMatchesMe) || {
    empId: myEmpId,
    employeeId: myEmpId,
    linkedAccountId: myIdentity.linkedAccountId,
    accountId: myIdentity.accountId,
    userId: myIdentity.userId,
    empName: myEmployee?.name || session?.name || "",
    items: [],
    conferences: [],
    viewedInspections: []
  };
  const epis   = sortAlpha(myList.items.filter(i => i.type === "EPI"), item => item?.name || item?.title || "");
  const equips = sortAlpha(myList.items.filter(i => i.type === "Equipamento"), item => item?.name || item?.title || "");

  const participantMatchesMe = (obj) => {
    const ids = [obj?.id, obj?.employeeId, obj?.userId, obj?.linkedAccountId, obj?.accountId].map(v => String(v || "").trim()).filter(Boolean);
    if (ids.some(v => v === myIdentity.employeeId || v === myIdentity.accountId || v === myIdentity.userId || v === myIdentity.linkedAccountId)) return true;
    const username = String(obj?.username || "").trim().toLowerCase();
    if (username && username === myIdentity.username) return true;
    return String(obj?.name || "").trim().toLowerCase() === myIdentity.name;
  };

  // Vistorias realizadas para mim (prioriza id único do colaborador e mantém fallback por nome)
  const myVistorias = inspections.filter(insp =>
    insp.status === "Finalizada" &&
    (insp.assignees || []).some(participantMatchesMe)
  );

  // Controle de "Não Visualizada / Visualizada" persistido na coleção checklists
  const viewedSet = new Set(myList.viewedInspections || []);

  function markViewed(inspId) {
    if (viewedSet.has(inspId)) return;
    const updated = {
      ...myList,
      empId: myList.empId || myEmpId,
      employeeId: myList.employeeId || myEmpId,
      linkedAccountId: myList.linkedAccountId || myIdentity.linkedAccountId,
      accountId: myList.accountId || myIdentity.accountId,
      userId: myList.userId || myIdentity.userId,
      empName: myList.empName || myEmployee?.name || session?.name || "",
      viewedInspections: [...(myList.viewedInspections || []), inspId],
      updatedAt: new Date().toISOString(),
    };
    const pending = markOperationalUpdatePending(updated, { userName: session?.name || "Sistema" });
    setChecklists(p => {
      const list = Array.isArray(p) ? p : [];
      const exists = list.find(checklistMatchesMe);
      return exists ? list.map(c => checklistMatchesMe(c) ? pending : c) : [...list, pending];
    });
    if (currentFarmId) syncOnSave(currentFarmId, "checklists");
  }

// eslint-disable-next-line no-unused-vars
  const _condMeta = (val) => ITEM_CONDITIONS.find(c => c.value === val) || ITEM_CONDITIONS[0];

  const ItemRow = ({ item }) => (
    <div style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px",
      background: item.withEmployee ? `${T.green}18` : `${T.muted}12`,
      borderRadius:8, marginBottom:6, border:`1px solid ${item.withEmployee ? T.green : T.border}` }}>
      <span style={{ fontSize:20 }}>{item.type === "EPI" ? "🦺" : "🔧"}</span>
      <div style={{ flex:1 }}>
        <div style={{ fontWeight:600, color:T.text, fontSize:14 }}>{item.name}</div>
        <div style={{ fontSize:12, color:T.muted, marginTop:2 }}>
          {item.withEmployee ? "✅ Em seu poder" : "↩️ Devolvido"}
          {item.deliveredAt ? ` · Entregue em ${item.deliveredAt}` : ""}
          {!item.withEmployee && item.returnedAt ? ` · Devolvido em ${item.returnedAt}` : ""}
        </div>
      </div>
    </div>
  );

  const VistoriaCard = ({ insp }) => {
    const viewed = viewedSet.has(insp.id);
    const nonConf = (insp.items || []).filter(i => i.status === "Não Conforme").length;
    const atencao = (insp.items || []).filter(i => i.status === "Atenção").length;
    return (
      <div onClick={() => markViewed(insp.id)} style={{ cursor:"pointer", padding:"12px 16px",
        background: viewed ? T.card : `${T.accent}15`,
        border:`1px solid ${viewed ? T.border : T.accent}`,
        borderRadius:10, marginBottom:8 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
          <div>
            <div style={{ fontWeight:700, color:T.text, fontSize:14 }}>{insp.targetLabel || "Vistoria"}</div>
            <div style={{ fontSize:12, color:T.muted, marginTop:2 }}>
              {insp.locationLabel || ""} · {insp.finishedAt ? new Date(insp.finishedAt).toLocaleDateString("pt-BR") : ""}
            </div>
          </div>
          <span style={{ fontSize:11, fontWeight:700, color: viewed ? T.muted : T.accent,
            background: viewed ? `${T.muted}22` : `${T.accent}22`,
            borderRadius:6, padding:"2px 8px", whiteSpace:"nowrap" }}>
            {viewed ? "Visualizada" : "Não Visualizada"}
          </span>
        </div>
        {(nonConf > 0 || atencao > 0) && (
          <div style={{ marginTop:8, display:"flex", gap:8 }}>
            {nonConf > 0 && <span style={{ fontSize:11, color:T.red }}>❌ {nonConf} não conforme{nonConf>1?"s":""}</span>}
            {atencao > 0 && <span style={{ fontSize:11, color:"#F59E0B" }}>⚠️ {atencao} atenção</span>}
          </div>
        )}
      </div>
    );
  };

  const totalEpisAtivos = epis.filter(i=>i.withEmployee).length;
  const totalEquipamentosAtivos = equips.filter(i=>i.withEmployee).length;
  const totalVistorias = myVistorias.length;
  const totalVisualizadas = myVistorias.filter(insp => viewedSet.has(insp.id)).length;

  const TABS = [
    { id:"epis",    label:`EPIs (${totalEpisAtivos})`,       icon:"🦺" },
    { id:"equips",  label:`Equipamentos (${totalEquipamentosAtivos})`, icon:"🔧" },
    { id:"vists",   label:`Vistorias (${totalVistorias})`,                     icon:"📋" },
  ];

  const ROOT_CARDS = {
    equipamentos: {
      icon:"🔧",
      label:"Equipamentos",
      value: totalEquipamentosAtivos,
      sub: totalEquipamentosAtivos === 1 ? "item em sua posse" : "itens em sua posse",
      color:T.accent,
      onClick:()=>setTab("equips"),
    },
    epis: {
      icon:"🦺",
      label:"EPIs",
      value: totalEpisAtivos,
      sub: totalEpisAtivos === 1 ? "item em sua posse" : "itens em sua posse",
      color:T.green,
      onClick:()=>setTab("epis"),
    },
    vistorias: {
      icon:"📋",
      label:"Vistorias",
      value: totalVistorias,
      sub: totalVistorias === 1 ? "vistoria vinculada" : "vistorias vinculadas",
      color:T.purple,
      onClick:()=>setTab("vists"),
    },
    itens_visualizados: {
      icon:"👁️",
      label:"Itens Visualizados",
      value: totalVisualizadas,
      sub: totalVistorias > 0 ? `${Math.max(totalVistorias - totalVisualizadas, 0)} pendente${Math.max(totalVistorias - totalVisualizadas, 0)===1?"":"s"}` : "sem pendências",
      color:T.yellow,
      onClick:()=>setTab("vists"),
    },
  };
  const visibleRootCards = allowedRootCards.map(id=>ROOT_CARDS[id]).filter(Boolean);

  return (
    <div style={{ maxWidth:600, margin:"0 auto", padding:"0 16px 40px" }}>
      <h2 style={{ fontFamily:"'Playfair Display',serif", fontSize:22, color:T.text, marginBottom:4 }}>🧰 Equipamentos & EPIs</h2>
      <p style={{ color:T.muted, fontSize:13, marginBottom:20 }}>Seus itens em posse e vistorias realizadas para você.</p>

      {!myEmployee && (
        <div style={{ background:`${T.red}18`, border:`1px solid ${T.red}`, borderRadius:10, padding:16, color:T.red }}>
          Registro de colaborador não encontrado para este usuário.
        </div>
      )}

      <>
          <ModuleHeaderMenu
            T={T}
            title="Menu de Equipamentos & EPIs"
            items={[
              { id:"my-items-home", label:"Início", icon:"⌂", active:false, onClick:()=>window.dispatchEvent(new CustomEvent("fz:module-header-menu:go-home")) },
              ...(myEmployee ? [
                { id:"my-items-epis", label:"EPIs", icon:"🦺", active:tab==="epis", onClick:()=>setTab("epis") },
                { id:"my-items-equips", label:"Equipamentos", icon:"🔧", active:tab==="equips", onClick:()=>setTab("equips") },
                { id:"my-items-vists", label:"Vistorias", icon:"📋", active:tab==="vists", onClick:()=>setTab("vists") },
              ] : []),
            ]}
          />

          {myEmployee && visibleRootCards.length > 0 && (
            <div style={{ display:"grid", gridTemplateColumns:"repeat(2, minmax(0, 1fr))", gap:12, marginBottom:18 }}>
              {visibleRootCards.map(card => (
                <StatCard
                  key={card.label}
                  icon={card.icon}
                  label={card.label}
                  value={card.value}
                  sub={card.sub}
                  color={card.color}
                  onClick={card.onClick}
                  cardStyle={{ minHeight: 138 }}
                />
              ))}
            </div>
          )}

          {myEmployee && (
          <>
          {/* Tabs */}
          <div style={{ display:"flex", gap:8, marginBottom:20, flexWrap:"wrap" }}>
            {TABS.map(t => (
              <button key={t.id} onClick={() => setTab(t.id)}
                style={{ padding:"6px 14px", borderRadius:20, cursor:"pointer", fontSize:13, fontWeight:600,
                  background: tab===t.id ? T.accent : T.card,
                  color:      tab===t.id ? "#fff"    : T.text,
                  border:`1px solid ${tab===t.id ? T.accent : T.border}` }}>
                {t.icon} {t.label}
              </button>
            ))}
          </div>

          {tab === "epis" && (
            <div>
              {epis.length === 0
                ? <p style={{ color:T.muted, fontSize:13 }}>Nenhum EPI registrado para você.</p>
                : epis.map(item => <ItemRow key={item.id} item={item} />)
              }
            </div>
          )}

          {tab === "equips" && (
            <div>
              {equips.length === 0
                ? <p style={{ color:T.muted, fontSize:13 }}>Nenhum equipamento registrado para você.</p>
                : equips.map(item => <ItemRow key={item.id} item={item} />)
              }
            </div>
          )}

          {tab === "vists" && (
            <div>
              {myVistorias.length === 0
                ? <p style={{ color:T.muted, fontSize:13 }}>Nenhuma vistoria realizada para você.</p>
                : myVistorias.map(insp => <VistoriaCard key={insp.id} insp={insp} />)
              }
            </div>
          )}
          </>
          )}
        </>
      {toast}
    </div>
  );
}

  return EquipamentosEPIsModule;
}
